// WebMCP: expose the knowledge base's key actions to in-browser AI agents.
// Spec: https://webmachinelearning.github.io/webmcp/
// Registration runs at page load. Browsers without the API are unaffected.

const modelContext = document.modelContext ?? navigator.modelContext;

if (modelContext && typeof modelContext.registerTool === 'function') {
    const origin = window.location.origin;
    const controller = new AbortController();

    const tools = [
        {
            name: 'search_knowledge_base',
            description: 'Search the Smart 145 knowledge base for articles. Returns article titles, topics, and canonical URLs for the first page of results.',
            inputSchema: {
                type: 'object',
                properties: {
                    query: {
                        type: 'string',
                        description: 'Keywords to search for in article titles and content.',
                    },
                },
                required: ['query'],
            },
            async execute({ query }) {
                const response = await fetch(`${origin}/search?q=${encodeURIComponent(query)}`, {
                    headers: { Accept: 'text/html' },
                });
                if (! response.ok) {
                    throw new Error(`Search failed with HTTP ${response.status}.`);
                }

                const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
                const results = Array.from(doc.querySelectorAll('article')).map((article) => {
                    const link = article.querySelector('h2 a');

                    return {
                        title: link?.textContent.trim() ?? '',
                        topic: article.querySelector('span')?.textContent.trim() ?? '',
                        url: link ? new URL(link.getAttribute('href'), origin).href : '',
                    };
                }).filter((result) => result.url !== '');

                return { content: [{ type: 'text', text: JSON.stringify({ query, results }) }] };
            },
        },
        {
            name: 'get_article',
            description: 'Fetch a Smart 145 knowledge base article, topic page, or the topic index as markdown. Pass a canonical URL or path on this site, for example /{topic}/{article}.',
            inputSchema: {
                type: 'object',
                properties: {
                    url: {
                        type: 'string',
                        description: 'Article or topic URL or path on this site.',
                    },
                },
                required: ['url'],
            },
            async execute({ url }) {
                const target = new URL(url, origin);
                if (target.origin !== origin) {
                    throw new Error('Only URLs on this site can be fetched.');
                }

                const response = await fetch(target.href, { headers: { Accept: 'text/markdown' } });
                if (! response.ok) {
                    throw new Error(`Fetch failed with HTTP ${response.status}.`);
                }

                return { content: [{ type: 'text', text: await response.text() }] };
            },
        },
    ];

    for (const tool of tools) {
        Promise.resolve(modelContext.registerTool(tool, { signal: controller.signal }))
            .catch((error) => console.error(`Failed to register WebMCP tool ${tool.name}:`, error));
    }

    // Unregister the tools when the page is unloaded.
    window.addEventListener('pagehide', () => controller.abort(), { once: true });
}
